/**
 * Summary Formatter Utility
 * Formats the first line of summary text to include date period and owner information
 * This ensures consistency between manual summary creation (frontend) and automated summary creation (backend)
 */

/**
 * Format the first line of a summary to include date period and owner info
 * @param {string} summaryText - The summary text to format
 * @param {Array} notes - Array of note objects with NoteDate property
 * @param {string} scope - 'my' (user's notes) or 'all' (all notes)
 * @param {string} ownerName - Name of the owner (user name)
 * @returns {string} - Formatted summary text
 */
export function formatSummaryFirstLine(summaryText, notes, scope, ownerName) {
  if (!summaryText || !notes || notes.length === 0) {
    return summaryText;
  }

  // Calculate date range from notes
  const noteDates = notes
    .map(note => note.NoteDate ? new Date(note.NoteDate) : null)
    .filter(date => date !== null);
    
  if (noteDates.length === 0) {
    return summaryText;
  }

  const minDate = new Date(Math.min(...noteDates));
  const maxDate = new Date(Math.max(...noteDates));

  const TIMEZONE = 'Asia/Bangkok';
  const TIMEZONE_LABEL = 'GMT+7';

  // Format dates with explicit timezone (Asia/Bangkok, GMT+7)
  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', {
      timeZone: TIMEZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  const startDate = formatDate(minDate);
  const endDate = formatDate(maxDate);
  const datePeriod = `${startDate} - ${endDate} (${TIMEZONE_LABEL})`;
  
  // Find and modify the first line that starts with ##
  const lines = summaryText.split('\n');
  let modified = false;
  
  for (let i = 0; i < lines.length; i++) {
    if (lines[i].trim().startsWith('##')) {
      // Add date period and owner info
      let newLine = lines[i];
      
      // Add date period
      newLine += ` (${datePeriod})`;
      
      // Add owner info if scope is "my"
      if (scope === 'my' && ownerName) {
        newLine += ` (only for ${ownerName})`;
      }
      
      lines[i] = newLine;
      modified = true;
      break;
    }
  }
  
  if (modified) {
    return lines.join('\n');
  }
  
  return summaryText;
}
